import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Plus, Trash2, Save, Eye, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { TEMPLATES } from "../lib/templates";

const newItem = () => ({ id: crypto.randomUUID(), description: "", quantity: 1, previous_quantity: 0, unit: "ud", unit_price: 0, discount_pct: 0, tax_pct: 21 });

const IRPF_OPTIONS = [
  { value: 0, label: "Sin IRPF" },
  { value: 7, label: "IRPF 7% (nuevos autónomos)" },
  { value: 15, label: "IRPF 15% (general)" },
  { value: 19, label: "IRPF 19%" },
];

const ESTIMATE_STATUSES = ["borrador", "enviado", "aceptado", "rechazado"];
const INVOICE_STATUSES = ["borrador", "enviado", "cobrado"];

export default function DocumentEditor({ type = "estimate" }) {
  const { id } = useParams();
  const isNew = !id || id === "nuevo" || id === "nueva";
  const navigate = useNavigate();
  const { user } = useAuth();

  const isInvoice = type === "invoice";
  const endpoint = isInvoice ? "invoices" : "estimates";
  const baseRoute = isInvoice ? "/app/facturas" : "/app/presupuestos";
  const label = isInvoice ? "factura" : "presupuesto";
  const Label_ = isInvoice ? "Factura" : "Presupuesto";

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [notes, setNotes] = useState("");
  const [templateId, setTemplateId] = useState(user?.company?.template_id || "classic");
  const [number, setNumber] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [reference, setReference] = useState("");
  const [project, setProject] = useState("");
  const [isOrigin, setIsOrigin] = useState(false);
  const [previousBilled, setPreviousBilled] = useState(0);
  const [contractTotal, setContractTotal] = useState(0);
  const [autoLoadingPrev, setAutoLoadingPrev] = useState(false);
  const [status, setStatus] = useState("borrador");
  const [irpf, setIrpf] = useState(user?.company?.irpf_default || 0);
  const [isp, setIsp] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", cif: "", email: "", phone: "", address: "" });

  const loadClients = () => api.get("/clients").then(r => setClients(r.data));

  useEffect(() => {
    loadClients();
    if (!isNew) {
      api.get(`/${endpoint}/${id}`).then(r => {
        const d = r.data;
        setClientId(d.client_id || "");
        setItems(d.items?.length ? d.items : [newItem()]);
        setNotes(d.notes || "");
        setTemplateId(d.template_id || "classic");
        setNumber(d.number || "");
        setIssueDate(d.issue_date || "");
        setValidUntil(d.valid_until || "");
        setDueDate(d.due_date || "");
        setPaymentMethod(d.payment_method || "");
        setBankAccount(d.bank_account || "");
        setReference(d.reference || "");
        setProject(d.project || "");
        setIsOrigin(!!d.is_origin);
        setPreviousBilled(d.previous_billed || 0);
        setContractTotal(d.contract_total || 0);
        setStatus(d.status || "borrador");
        setIrpf(d.irpf_pct || 0);
        setIsp(!!d.isp);
      });
    } else {
      setNotes(user?.company?.notes_default || "");
      // Sensible default issue_date for invoices: today
      if (isInvoice) setIssueDate(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line
  }, [id, type]);

  const subtotal = items.reduce((s, i) => {
    const q = (i.quantity || 0) + (isOrigin ? (i.previous_quantity || 0) : 0);
    return s + q * i.unit_price * (1 - (i.discount_pct || 0) / 100);
  }, 0);
  const previousCertified = isOrigin ? items.reduce((s, i) => s + (i.previous_quantity || 0) * i.unit_price * (1 - (i.discount_pct || 0) / 100), 0) : 0;
  const monthlyBase = isOrigin ? subtotal - previousCertified : subtotal;
  const tax = isp ? 0 : items.reduce((s, i) => {
    const base = isOrigin ? (i.quantity || 0) * i.unit_price * (1 - (i.discount_pct || 0) / 100)
                          : (i.quantity || 0) * i.unit_price * (1 - (i.discount_pct || 0) / 100);
    return s + base * (i.tax_pct || 0) / 100;
  }, 0);
  const irpfAmount = monthlyBase * (irpf || 0) / 100;
  const total = monthlyBase + tax - irpfAmount;

  const fetchPreviousBilled = async () => {
    if (!project) { toast.error("Indica primero la obra/proyecto"); return; }
    setAutoLoadingPrev(true);
    try {
      const r = await api.get(`/projects/${encodeURIComponent(project)}/last-items`, { params: id && id !== "nueva" ? { exclude_id: id } : {} });
      if (!r.data.items?.length) {
        toast.warning("No hay facturas anteriores para esta obra");
        return;
      }
      setItems(r.data.items);
      if (r.data.contract_total && !contractTotal) setContractTotal(r.data.contract_total);
      toast.success(`Cargadas ${r.data.items.length} partidas desde ${r.data.source_invoice?.number}`);
    } catch { toast.error("No se pudo cargar"); }
    finally { setAutoLoadingPrev(false); }
  };

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: field === "description" || field === "unit" ? value : parseFloat(value) || 0 };
    setItems(next);
  };

  const generateAI = async () => {
    if (!aiPrompt.trim()) { toast.error("Describe brevemente el trabajo"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate-items", { prompt: aiPrompt, context: user?.company?.name });
      const generated = r.data.items || [];
      if (!generated.length) { toast.error("La IA no devolvió partidas"); return; }
      const existing = items.filter(i => i.description.trim());
      setItems([...existing, ...generated]);
      setAiPrompt("");
      toast.success(`${generated.length} partidas generadas`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data?.detail || "Has alcanzado el límite de IA del plan Free", {
          action: { label: "Hazte Pro", onClick: () => navigate("/app/planes") },
          duration: 8000,
        });
      } else {
        toast.error(e.response?.data?.detail || "Error al generar con IA");
      }
    }
    finally { setAiLoading(false); }
  };

  const createNewClient = async () => {
    if (!newClient.name) { toast.error("Nombre obligatorio"); return; }
    try {
      const r = await api.post("/clients", newClient);
      toast.success("Cliente creado");
      await loadClients();
      setClientId(r.data.id);
      setClientDialogOpen(false);
      setNewClient({ name: "", cif: "", email: "", phone: "", address: "" });
    } catch { toast.error("Error al crear cliente"); }
  };

  const save = async (afterRedirect = true) => {
    setSaving(true);
    const body = {
      client_id: clientId || null,
      items, notes, template_id: templateId,
      number: number || null,
      issue_date: issueDate || null,
      valid_until: validUntil || null,
      due_date: dueDate || null,
      payment_method: paymentMethod || null,
      bank_account: bankAccount || null,
      reference: reference || null,
      project: project || null,
      is_origin: isOrigin,
      previous_billed: parseFloat(previousBilled) || 0,
      contract_total: parseFloat(contractTotal) || 0,
      status, irpf_pct: irpf, isp,
    };
    try {
      let res;
      if (isNew) res = await api.post(`/${endpoint}`, body);
      else res = await api.put(`/${endpoint}/${id}`, body);
      toast.success("Guardado");
      if (afterRedirect) navigate(`${baseRoute}/${res.data.id}`);
      return res.data;
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data?.detail || "Has alcanzado el límite del plan Free", {
          action: { label: "Hazte Pro", onClick: () => navigate("/app/planes") },
          duration: 8000,
        });
      } else {
        toast.error("Error al guardar");
      }
    }
    finally { setSaving(false); }
  };

  const statusOptions = isInvoice ? INVOICE_STATUSES : ESTIMATE_STATUSES;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-editor">
      <header className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">{isNew ? "Nuevo" : "Editar"} {label}</div>
          <h1 className="font-display font-bold text-4xl tracking-tight">{isNew ? `Crear ${label}` : `Editar ${label}`}</h1>
        </div>
        <div className="flex gap-2">
          {!isNew && <Button data-testid="view-doc" variant="outline" onClick={() => navigate(`${baseRoute}/${id}`)} className="border-white/20 bg-transparent text-white hover:bg-white/5"><Eye className="w-4 h-4 mr-2" /> Ver</Button>}
          <Button data-testid="save-doc-btn" onClick={() => save()} disabled={saving} className="bg-brand-orange hover:bg-brand-orange-hover"><Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar"}</Button>
        </div>
      </header>

      <section className="rounded-lg border border-brand-cyan/30 bg-brand-cyan/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-brand-cyan" />
          <h2 className="font-display font-semibold text-lg">Asistente IA · genera partidas en segundos</h2>
        </div>
        <p className="text-sm text-white/60 mb-3">Describe brevemente lo que vas a {isInvoice ? "facturar" : "presupuestar"} y la IA generará las partidas con descripción, cantidades y precios sugeridos.</p>
        <div className="flex gap-2">
          <Input data-testid="ai-prompt-input" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Ej: instalación de aire acondicionado split en salón 25m2" className="bg-black/50 border-white/10 text-white" />
          <Button data-testid="ai-generate-btn" onClick={generateAI} disabled={aiLoading} className="bg-brand-cyan hover:bg-brand-cyan-hover text-surface-base font-semibold whitespace-nowrap">
            {aiLoading ? "Generando..." : <><Sparkles className="w-4 h-4 mr-1" /> Generar</>}
          </Button>
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Nº de {label}</Label>
          <Input data-testid="doc-number" value={number} onChange={(e) => setNumber(e.target.value)} placeholder={isNew ? "Auto (P-2026-0001)" : ""} className="bg-black/50 border-white/10 text-white font-mono" />
        </div>
        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Fecha de emisión</Label>
          <Input data-testid="doc-issue-date" type="date" value={issueDate || ""} onChange={(e) => setIssueDate(e.target.value)} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="status-select" className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A1128] border-white/10 text-white">
              {statusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 lg:col-span-2">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Cliente</Label>
          <div className="flex gap-2">
            <Select value={clientId || "_none"} onValueChange={(v) => setClientId(v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="client-select" className="bg-black/50 border-white/10 text-white"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
              <SelectContent className="bg-[#0A1128] border-white/10 text-white">
                <SelectItem value="_none">Sin cliente</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="inline-new-client" type="button" variant="outline" onClick={() => setClientDialogOpen(true)} className="border-brand-cyan/40 bg-transparent text-brand-cyan hover:bg-brand-cyan/10 whitespace-nowrap">
              <UserPlus className="w-4 h-4 mr-1" /> Nuevo
            </Button>
          </div>
        </div>
        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Plantilla</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger data-testid="template-select" className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A1128] border-white/10 text-white">
              {TEMPLATES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {!isInvoice && (
          <div className="space-y-2 lg:col-span-1">
            <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Válido hasta</Label>
            <Input type="date" value={validUntil || ""} onChange={(e) => setValidUntil(e.target.value)} className="bg-black/50 border-white/10 text-white" />
          </div>
        )}
        {isInvoice && (
          <div className="space-y-2 lg:col-span-1">
            <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Vencimiento</Label>
            <Input data-testid="due-date" type="date" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)} className="bg-black/50 border-white/10 text-white" />
          </div>
        )}

        <div className="space-y-2 lg:col-span-2">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Referencia / Nº de obra <span className="text-white/40 normal-case font-normal">— opcional</span></Label>
          <Input data-testid="doc-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={isInvoice ? "Ej: PED-2026-014, Obra 3, Contrato X…" : "Ej: REF-001, Pedido cliente, Obra principal…"} className="bg-black/50 border-white/10 text-white" />
          {!isInvoice && <p className="text-[11px] text-white/40">Se heredará automáticamente en las facturas que generes de este presupuesto.</p>}
        </div>

        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Retención IRPF</Label>
          <Select value={String(irpf)} onValueChange={(v) => setIrpf(parseFloat(v))}>
            <SelectTrigger data-testid="irpf-select" className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A1128] border-white/10 text-white">
              {IRPF_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Régimen fiscal</Label>
          <label className="flex items-center gap-3 p-2.5 rounded border border-white/10 bg-black/30 cursor-pointer hover:bg-black/40 transition-colors">
            <Checkbox data-testid="isp-checkbox" checked={isp} onCheckedChange={setIsp} className="border-white/30 data-[state=checked]:bg-brand-cyan data-[state=checked]:text-surface-base" />
            <span className="text-sm">Inversión sujeto pasivo</span>
          </label>
        </div>
      </div>

      {isInvoice && (
        <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-5 space-y-4">
          <h2 className="font-display font-semibold text-lg">Obra / proyecto y facturación a origen</h2>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Obra / proyecto</Label>
              <Input data-testid="doc-project" value={project} onChange={(e) => setProject(e.target.value)} placeholder="Ej: Reforma local C/ Mayor 14" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Modo de facturación</Label>
              <label className="flex items-center gap-3 p-2.5 rounded border border-white/10 bg-black/30 cursor-pointer hover:bg-black/40 transition-colors">
                <Checkbox data-testid="is-origin-checkbox" checked={isOrigin} onCheckedChange={setIsOrigin} className="border-white/30 data-[state=checked]:bg-brand-cyan data-[state=checked]:text-surface-base" />
                <span className="text-sm">Factura a origen (acumulativa por partidas)</span>
              </label>
            </div>
            {isOrigin && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Total contrato (€) <span className="text-white/40 normal-case font-normal">— opcional</span></Label>
                <Input data-testid="doc-contract-total" type="number" step="0.01" value={contractTotal} onChange={(e) => setContractTotal(e.target.value)} placeholder="Importe total contratado de la obra" className="bg-black/50 border-white/10 text-white" />
                <p className="text-xs text-white/40">Te avisaremos en cada certificación cuánto queda para llegar al total y evitar pasarte.</p>
              </div>
            )}
            {isOrigin && project && (
              <div className="lg:col-span-2">
                <Button data-testid="load-previous-items" type="button" variant="outline" onClick={fetchPreviousBilled} disabled={autoLoadingPrev} className="border-brand-cyan/40 bg-transparent text-brand-cyan hover:bg-brand-cyan/10">
                  {autoLoadingPrev ? "Cargando..." : "Cargar partidas y mediciones del mes anterior"}
                </Button>
                <p className="text-xs text-white/40 mt-2">Carga automáticamente las partidas de la última factura de esta obra, poniendo las mediciones anteriores ya rellenadas y la actual a 0.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {isInvoice && (
        <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-5 space-y-4">
          <h2 className="font-display font-semibold text-lg">Forma de pago</h2>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Forma de pago</Label>
              <Input data-testid="payment-method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} placeholder="Transferencia, Bizum, Efectivo, Tarjeta…" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">IBAN / Nº de cuenta</Label>
              <Input data-testid="bank-account" value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} placeholder="ES00 0000 0000 00 0000000000" className="bg-black/50 border-white/10 text-white font-mono" />
            </div>
          </div>
        </section>
      )}

      <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <h2 className="font-display font-semibold">Partidas</h2>
          <Button data-testid="add-item-btn" size="sm" variant="ghost" onClick={() => setItems([...items, newItem()])} className="text-brand-cyan hover:bg-brand-cyan/10"><Plus className="w-4 h-4 mr-1" /> Añadir línea</Button>
        </div>
        {isOrigin && (
          <div className="px-4 pt-3 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest font-bold text-white/50">
            <div className="col-span-5">Descripción</div>
            <div className="col-span-1 text-right">Med. actual</div>
            <div className="col-span-1 text-right">Med. anterior</div>
            <div className="col-span-1 text-right">A origen</div>
            <div className="col-span-2 text-right">Precio</div>
            <div className="col-span-1 text-right">IVA</div>
            <div className="col-span-1 text-right">Total</div>
          </div>
        )}
        <div className="divide-y divide-white/5">
          {items.map((it, idx) => {
            const medOrigen = (it.quantity || 0) + (isOrigin ? (it.previous_quantity || 0) : 0);
            const lineTotal = medOrigen * (it.unit_price || 0) * (1 - (it.discount_pct || 0) / 100);
            return (
              <div key={it.id} className="p-4 grid grid-cols-12 gap-2 items-start" data-testid={`item-row-${idx}`}>
                <Textarea data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Descripción de la partida" rows={2} className="col-span-12 md:col-span-5 bg-black/50 border-white/10 text-white text-sm" />
                <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} placeholder={isOrigin ? "Actual" : "Cant."} className="col-span-3 md:col-span-1 bg-black/50 border-white/10 text-white text-sm" />
                {isOrigin ? (
                  <>
                    <Input data-testid={`item-prev-${idx}`} type="number" step="0.01" value={it.previous_quantity || 0} onChange={(e) => updateItem(idx, "previous_quantity", e.target.value)} placeholder="Anterior" className="col-span-3 md:col-span-1 bg-black/50 border-white/10 text-white text-sm" />
                    <div className="col-span-2 md:col-span-1 text-right text-sm pt-2 text-brand-cyan font-mono">{medOrigen.toFixed(2)}</div>
                  </>
                ) : (
                  <Input value={it.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} placeholder="Ud" className="col-span-3 md:col-span-1 bg-black/50 border-white/10 text-white text-sm" />
                )}
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} placeholder="Precio" className="col-span-3 md:col-span-2 bg-black/50 border-white/10 text-white text-sm" />
                <Input type="number" step="0.01" value={it.tax_pct} onChange={(e) => updateItem(idx, "tax_pct", e.target.value)} placeholder="IVA%" disabled={isp} className="col-span-3 md:col-span-1 bg-black/50 border-white/10 text-white text-sm disabled:opacity-40" />
                <div className="col-span-10 md:col-span-1 text-right font-semibold text-sm pt-2">{eur(lineTotal)}</div>
                <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-2 md:col-span-1 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
              </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end">
          <div className="w-80 space-y-1 text-sm">
            {isOrigin ? <>
              <div className="flex justify-between"><span className="text-white/60">Total certificado a origen</span><span>{eur(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Certificaciones anteriores</span><span className="text-red-300">-{eur(previousCertified)}</span></div>
              <div className="flex justify-between pt-2 border-t border-white/10"><span className="text-white/60">Base imp. del mes</span><span>{eur(monthlyBase)}</span></div>
            </> : <div className="flex justify-between"><span className="text-white/60">Base imponible</span><span>{eur(subtotal)}</span></div>}
            {!isp && <div className="flex justify-between"><span className="text-white/60">IVA</span><span>{eur(tax)}</span></div>}
            {isp && <div className="text-xs text-amber-400/80 italic py-1">Operación con inversión del sujeto pasivo (art. 84 LIVA)</div>}
            {irpf > 0 && <div className="flex justify-between"><span className="text-white/60">IRPF ({irpf}%)</span><span className="text-red-300">-{eur(irpfAmount)}</span></div>}
            <div className="flex justify-between font-display font-bold text-xl mt-2 pt-2 border-t border-white/10"><span>{isOrigin ? "A facturar" : "Total"}</span><span className="text-brand-cyan">{eur(total)}</span></div>
            {isOrigin && parseFloat(contractTotal) > 0 && (() => {
              const ct = parseFloat(contractTotal) || 0;
              const pct = Math.min(100, (subtotal / ct) * 100);
              const remaining = ct - subtotal;
              const over = remaining < 0;
              return (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5" data-testid="contract-progress-editor">
                  <div className="flex justify-between text-sm"><span className="text-white/60">Total contrato</span><span className="font-semibold">{eur(ct)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Facturado a la fecha</span><span>{eur(subtotal)}</span></div>
                  <div className={`flex justify-between text-sm font-semibold ${over ? "text-red-400" : "text-emerald-400"}`}>
                    <span>{over ? "Excedido del contrato" : "Pendiente del contrato"}</span>
                    <span>{eur(Math.abs(remaining))}</span>
                  </div>
                  <div className="h-2 bg-black/40 rounded overflow-hidden mt-1">
                    <div className={`h-full transition-all ${over ? "bg-red-500" : pct > 90 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="text-[10px] text-white/40 text-right">{pct.toFixed(1)}% del contrato</div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      <div>
        <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Notas / condiciones</Label>
        <Textarea data-testid="doc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5 bg-black/50 border-white/10 text-white" />
      </div>

      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Nuevo cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              ["name", "Nombre / Razón social *"],
              ["cif", "CIF / NIF"],
              ["email", "Email"],
              ["phone", "Teléfono"],
              ["address", "Dirección"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest font-bold text-white/70">{l}</Label>
                <Input data-testid={`inline-client-${k}`} value={newClient[k] || ""} onChange={(e) => setNewClient({ ...newClient, [k]: e.target.value })} className="mt-1.5 bg-black/50 border-white/10 text-white" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientDialogOpen(false)} className="border-white/20 bg-transparent">Cancelar</Button>
            <Button data-testid="save-inline-client" onClick={createNewClient} className="bg-brand-orange hover:bg-brand-orange-hover">Guardar y seleccionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
